/**
 * Pre-registers Dawn's `facet-filters-form` and `facet-remove` custom
 * elements with constructors that are safe against Preact's deferred
 * children mount.
 *
 * Dawn's original constructors do:
 *   - `this.querySelector('form').addEventListener(...)` (FacetFiltersForm)
 *   - `this.querySelector('a').setAttribute('role', 'button')` (FacetRemove)
 *
 * Preact mounts the wrapper element BEFORE its children, so the
 * `querySelector` call returns null and the browser throws — visible in
 * the console as
 *   `Cannot read properties of null (reading 'addEventListener')` and
 *   `Cannot read properties of null (reading 'setAttribute')`.
 *
 * Our safe versions defer all work to `connectedCallback` AFTER children
 * exist (we poll once with a microtask, since Preact mounts synchronously
 * within the same task). The CSS rules that target the tag name keep
 * applying because the tag is still `facet-filters-form` / `facet-remove`.
 *
 * Loaded synchronously in `<head>` BEFORE `content_for_header` so the
 * native Dawn `facets.js` (loaded later by sections via defer) sees the
 * name as already registered and its `customElements.define` call no-ops
 * silently via the wrapped global below.
 */
(function () {
	if (typeof customElements === 'undefined') return;

	// Silently absorb Dawn's redefine attempts so its script can keep
	// running past the `customElements.define('facet-filters-form', …)`
	// line (which would otherwise throw `NotSupportedError` and stop
	// execution before the popstate listener / price-range define).
	var nativeDefine = customElements.define.bind(customElements);
	customElements.define = function (name, ctor, options) {
		if (customElements.get(name)) return; // ours wins
		return nativeDefine(name, ctor, options);
	};

	function safeBindForm(host) {
		var form = host.querySelector('form');
		if (!form) return;
		// Dawn's FacetFiltersForm binds debounced input → onSubmitHandler.
		// We don't reimplement that (our Preact owns form submission), but
		// the empty wrapper element keeps the tag for CSS purposes.
	}

	function safeBindRemove(host) {
		var link = host.querySelector('a');
		if (!link) return;
		link.setAttribute('role', 'button');
		// Click handling is owned by Preact's onClick in the JSX — no need
		// to attach a duplicate listener here.
	}

	function mountWhenReady(host, fn) {
		// Run once now (covers SSR'd Liquid skeletons) and once more on the
		// next microtask (covers Preact-deferred children).
		fn(host);
		Promise.resolve().then(function () { fn(host); });
	}

	class NostoFacetFiltersForm extends HTMLElement {
		constructor() { super(); }
		connectedCallback() { mountWhenReady(this, safeBindForm); }
	}

	class NostoFacetRemove extends HTMLElement {
		constructor() { super(); }
		connectedCallback() { mountWhenReady(this, safeBindRemove); }
	}

	nativeDefine('facet-filters-form', NostoFacetFiltersForm);
	nativeDefine('facet-remove', NostoFacetRemove);
})();
